import { Fragment, type ReactNode, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from '@repo/design-system-kit';
import { useSegment } from '@repo/poly-hooks/segment';
import type { PropertyOption } from '../../types';
import { FiltersListItem, type FiltersListItemProps } from './filters-list-item/filters-list-item';
import { FiltersListHeader } from './filters-list-header/filters-list-header';
import { AddFilterButton } from './add-filter-button/add-filter-button';
import styles from './filters-list.module.css';

export const ADVANCED_FILTER_KEY = 'advanced-filter';

const RESET_FILTERS_TRACKING_EVENT = 'reset_filters_clicked';

interface FilterEntry {
  filter: PropertyOption;
  itemProps: FiltersListItemProps;
}

interface FiltersListProps {
  availableFilters: PropertyOption[];
  pinnedFilters: string[];
  activeFilters?: string[];
  advancedFilterCount?: number;
  hasAdvancedFilters?: boolean;
  filterToOpen?: { key: string } | null;
  onFilterSelect?: (filter: PropertyOption) => void;
  onResetFilters?: () => void;
  children: (filter: PropertyOption, itemProps: FiltersListItemProps) => ReactNode;
}

function FiltersListComponent({
  availableFilters,
  pinnedFilters,
  activeFilters = [],
  advancedFilterCount = 0,
  hasAdvancedFilters = false,
  filterToOpen,
  onFilterSelect,
  onResetFilters,
  children,
}: FiltersListProps) {
  const { formatMessage } = useIntl();
  const segment = useSegment();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(advancedFilterCount > 0);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [pendingFilter, setPendingFilter] = useState<PropertyOption | null>(null);
  const [activePinnedFilters, setActivePinnedFilters] = useState<string[]>(
    activeFilters.filter(key => pinnedFilters.includes(key))
  );
  const [hasFilterOpen, setHasFilterOpen] = useState(false);

  useEffect(() => {
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (advancedFilterCount > 0) {
      // oxlint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
      setShowAdvancedFilters(true);
    }
  }, [advancedFilterCount]);

  useEffect(() => {
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-derived-state, react-you-might-not-need-an-effect/no-pass-data-to-parent, react-you-might-not-need-an-effect/no-pass-live-state-to-parent
    setActivePinnedFilters(prev => {
      // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      const removed = prev.filter(f => activeFilters.includes(f));
      // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      const added = !hasFilterOpen
        ? // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
          activeFilters.filter(key => pinnedFilters.includes(key) && !prev.includes(key))
        : [];

      const next = [...removed, ...added];

      // Bail out if nothing changed — avoids infinite re-render loop
      if (next.length === prev.length && next.every((v, i) => v === prev[i])) {
        return prev;
      }
      return next;
    });
  }, [activeFilters, hasFilterOpen, pinnedFilters]);

  // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
  const consumedFilterToOpenRef = useRef<typeof filterToOpen>(null);

  useEffect(() => {
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (!filterToOpen) {
      consumedFilterToOpenRef.current = null;
      return;
    }
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (consumedFilterToOpenRef.current === filterToOpen) return;
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-pass-data-to-parent
    const target = availableFilters.find(f => f.key === filterToOpen.key);
    if (target) {
      consumedFilterToOpenRef.current = filterToOpen;
      // oxlint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
      setPendingFilter(target);
    }
  }, [filterToOpen, availableFilters]);

  const shownFilterKeys = new Set([...activeFilters, ...pinnedFilters]);

  const handlePinnedClose = (key: string) => {
    if (activeFilters.includes(key)) {
      setActivePinnedFilters(prev => [...prev, key]);
    } else {
      setActivePinnedFilters(prev => prev.filter(k => k !== key));
    }
  };

  const handleFilterOpen = () => {
    setHasFilterOpen(true);
  };

  const findFilter = (key: string) => availableFilters.find(f => f.key === key);

  const activeEntries: FilterEntry[] = activeFilters.flatMap(key => {
    const filter = findFilter(key);
    if (!filter) return [];

    const isTransitioningPinned = pinnedFilters.includes(key) && !activePinnedFilters.includes(key);
    const isTransitioningPending = pendingFilter?.key === key;
    const needsCleanup = isTransitioningPinned || isTransitioningPending;

    return [
      {
        filter,
        itemProps: {
          ...(isTransitioningPending && { autoOpen: true }),
          onOpen: handleFilterOpen,
          ...(needsCleanup && {
            onClose: () => {
              if (isTransitioningPending) setPendingFilter(null);
              if (isTransitioningPinned) handlePinnedClose(key);
            },
          }),
        },
      },
    ];
  });

  const pinnedEntries: FilterEntry[] = pinnedFilters.flatMap(key => {
    if (activeFilters.includes(key) || activePinnedFilters.includes(key)) return [];
    const filter = findFilter(key);
    if (!filter) return [];

    const isTransitioningPending = pendingFilter?.key === key;

    return [
      {
        filter,
        itemProps: {
          ...(isTransitioningPending && { autoOpen: true }),
          onOpen: handleFilterOpen,
          onClose: () => {
            if (isTransitioningPending) setPendingFilter(null);
            handlePinnedClose(key);
          },
        },
      },
    ];
  });

  const pendingEntries: FilterEntry[] =
    pendingFilter &&
    !activeFilters.includes(pendingFilter.key) &&
    !pinnedFilters.includes(pendingFilter.key)
      ? [
          {
            filter: pendingFilter,
            itemProps: {
              autoOpen: true,
              onOpen: handleFilterOpen,
              onClose: () => {
                setPendingFilter(null);
              },
            },
          },
        ]
      : [];

  const filterEntries: FilterEntry[] = [...activeEntries, ...pinnedEntries, ...pendingEntries];

  return (
    <div className={styles.container}>
      {showAdvancedFilters ? (
        <Fragment key="advanced-filters">
          {children(
            {
              key: ADVANCED_FILTER_KEY,
              label: '',
            },
            {
              autoOpen: advancedFiltersOpen,
              onOpen: () => {
                setHasFilterOpen(true);
              },
              onClose: () => {
                if (!advancedFilterCount) {
                  setShowAdvancedFilters(false);
                }
              },
            }
          )}
        </Fragment>
      ) : null}

      {filterEntries.map(({ filter, itemProps }) => (
        <Fragment key={filter.key}>{children(filter, itemProps)}</Fragment>
      ))}

      <AddFilterButton
        hasAdvancedFilters={Boolean(hasAdvancedFilters && !advancedFilterCount)}
        onSelect={key => {
          if (key === 'advanced-filter') {
            setAdvancedFiltersOpen(true);
            setShowAdvancedFilters(true);
            return;
          }

          const selectedFilter = availableFilters.find(f => f.key === key);
          setPendingFilter(selectedFilter ?? null);
          if (selectedFilter) {
            onFilterSelect?.(selectedFilter);
          }
        }}
        options={availableFilters
          .filter(filter => !filter.hidden && !shownFilterKeys.has(filter.key))
          .map(filter => ({
            label: filter.label,
            value: filter.key,
            color: filter.color,
          }))}
      />

      {activeFilters.length > 0 || advancedFilterCount > 0 ? (
        <Button
          className={styles.resetFiltersButton}
          data-testid="filters-list-reset-button"
          onPress={() => {
            segment.track(RESET_FILTERS_TRACKING_EVENT);
            onResetFilters?.();
          }}
          size="small"
          variant="tertiary"
        >
          {formatMessage({ id: 'shared-kit.filters.reset-filters' })}
        </Button>
      ) : null}
    </div>
  );
}

export const FiltersList = Object.assign(FiltersListComponent, {
  Header: FiltersListHeader,
  ListItem: FiltersListItem,
});
